import React, { useState, useEffect } from "react";
import BatchImportPresetWinnersDialogComponent from "./batchImportPresetWinnersDialog.component";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { getApiLang } from "app/feature/constants";
import { addAlert } from "modules/notification";
import { 
  validateBatchWinnerImport, 
  importBatchWinners, 
  downloadBatchWinnerTemplate 
} from "app/api/luckyDrawV2/validateWinnerImport.api";

function BatchImportPresetWinnersDialogContainer({ 
  isOpen, 
  handleClose, 
  luckyDrawUuid,
  prizes,
  onImportSuccess
}) {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.constant.languages);
  
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
  const [file, setFile] = useState(null);
  const [validationResult, setValidationResult] = useState(null);
  const [filePath, setFilePath] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [showValidationResult, setShowValidationResult] = useState(false);

  useEffect(() => {
    if (isOpen) {
      // Reset state when dialog opens
      setFile(null);
      setValidationResult(null);
      setFilePath(null);
      setActiveStep(0);
      setShowValidationResult(false);
    }
  }, [isOpen]);

  const handleFileChange = (newFile) => {
    setFile(newFile);
    setValidationResult(null);
    setFilePath(null);
    setShowValidationResult(false);
    setActiveStep(0);
  };

  const handleValidate = async () => {
    if (!file) {
      dispatch(
        addAlert({
          severity: "warning",
          message: getApiLang(lang, "error.ERROR_NO_FILE_SELECTED"),
        })
      );
      return;
    }

    setIsValidating(true);
    
    try {
      const response = await validateBatchWinnerImport({
        file,
        luckyDrawUuid
      });

      if (response.success) {
        setValidationResult(response.data.validation_data);
        setFilePath(response.data.file_path);
        setShowValidationResult(true);
        setActiveStep(1);
        
        dispatch(
          addAlert({
            severity: "success",
            message: getApiLang(lang, "success.SUCCESS_FILE_VALIDATED_SUCCESSFULLY"),
          })
        );
      } else {
        handleValidationError(response);
      }
    } catch (error) {
      handleValidationError(error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleValidationError = (error) => {
    let errorMessage = getApiLang(lang, error.code || "error.ERROR_SOMETHING_WRONG");
    
    // Check if this is a validation error with invalid serial numbers
    if (error.code === "error.ERROR_INVALID_SERIAL_NUMBERS_LUCKYDRAW_IMPORT" ||
        error.code === "error.ERROR_INVALID_CSV_FILE") {
      
             // Check if error has validation data with sheets
       if (error.data && error.data.sheets) {
         // Set the validation result to show the invalid serials table
         setValidationResult(error.data);
         setShowValidationResult(true);
         setActiveStep(1);
         
         errorMessage = getApiLang(lang, "message.VALIDATION_ERRORS_FOUND") || 
                      "Validation errors found. Please review the details below.";
             } else if (error.data && (error.data.invalid_serials || error.data.valid_serials)) {

         const singleSheetResult = {
           success: false,
           sheets: {
             'Unknown Sheet': {
               success: false,
               error: error.code,
               data: {
                 all_serials_status: {
                   valid: error.data.valid_serials || [],
                   invalid: error.data.invalid_serials || []
                 }
               },
               prize_name: 'Unknown Prize'
             }
           }
         };
         
         setValidationResult(singleSheetResult);
         setShowValidationResult(true);
         setActiveStep(1);
         
         errorMessage = getApiLang(lang, "message.VALIDATION_ERRORS_FOUND") || 
                      "Validation errors found. Please review the details below.";
      }
    }
    
    if (error.message && typeof error.message === 'string') {
      errorMessage = error.message;
    } else if (error.data && error.data.message) {
      errorMessage += `: ${error.data.message}`;
    }
    
    dispatch(
      addAlert({
        severity: "error",
        message: errorMessage,
      })
    );
  };

  const handleSubmit = async () => {
    if (!filePath || !validationResult) {
      dispatch(
        addAlert({
          severity: "warning",
          message: getApiLang(lang, "error.ERROR_NO_VALIDATED_FILE"),
        })
      );
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await importBatchWinners({
        file_path: filePath,
        lucky_draw_uuid: luckyDrawUuid,
        validation_data: validationResult
      });

      if (response.success) {
        dispatch(
          addAlert({
            severity: "success",
            message: `Successfully imported ${response.data.total_imported} preset winners across ${Object.keys(response.data.sheets).length} sheets.`,
          })
        );
        
        handleClose();
        if (onImportSuccess) {
          onImportSuccess();
        }
      } else {
        handleValidationError(response);
      }
    } catch (error) {
      handleValidationError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleTemplateDownload = async () => {
    setIsDownloadingTemplate(true);
    
    try {
      const response = await downloadBatchWinnerTemplate(luckyDrawUuid);
      
      // Create blob and download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Extract filename from Content-Disposition header if available
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'batch_winners_template.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      dispatch(
        addAlert({
          severity: "success",
          message: getApiLang(lang, "success.SUCCESS_TEMPLATE_DOWNLOADED"),
        })
      );
    } catch (error) {
      dispatch(
        addAlert({
          severity: "error",
          message: getApiLang(lang, error.code || "error.ERROR_TEMPLATE_DOWNLOAD_FAILED"),
        })
      );
    } finally {
      setIsDownloadingTemplate(false);
    }
  };

  const handleStartOver = () => {
    setFile(null);
    setValidationResult(null);
    setFilePath(null);
    setActiveStep(0);
    setShowValidationResult(false);
  };

  const handleCloseDialog = () => {
    setFile(null);
    setValidationResult(null);
    setFilePath(null);
    setActiveStep(0);
    setShowValidationResult(false);
    handleClose();
  };

  return (
    <BatchImportPresetWinnersDialogComponent
      isOpen={isOpen}
      handleClose={handleCloseDialog}
      lang={lang}
      addAlert={(alert) => dispatch(addAlert(alert))}
      file={file}
      handleFileChange={handleFileChange}
      handleValidate={handleValidate}
      handleSubmit={handleSubmit}
      handleTemplateDownload={handleTemplateDownload}
      handleStartOver={handleStartOver}
      isValidating={isValidating}
      isSubmitting={isSubmitting}
      isDownloadingTemplate={isDownloadingTemplate}
      validationResult={validationResult}
      showValidationResult={showValidationResult}
      activeStep={activeStep}
      luckyDrawUuid={luckyDrawUuid}
      prizes={prizes}
    />
  );
}

BatchImportPresetWinnersDialogContainer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  luckyDrawUuid: PropTypes.string.isRequired,
  prizes: PropTypes.array.isRequired,
  onImportSuccess: PropTypes.func,
};

export default BatchImportPresetWinnersDialogContainer; 